import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, MessageCircle, CheckCircle2 } from "lucide-react";
import { useExplorer } from "@/context/ExplorerContext";
import { Eyebrow, Section, Tape, WhatsAppButton } from "@/components/lurio/Primitives";
import { Reveal, EASE } from "@/components/lurio/Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PriceTag = ({ t, qty }) => (
  <div data-testid="price-tag" className="relative inline-flex rotate-[3deg] items-center">
    <span aria-hidden className="h-5 w-5 rounded-full border-2 border-ink/30 bg-cream" />
    <span aria-hidden className="h-0.5 w-6 bg-ink/30" />
    <div className="paper-card grain flex flex-col items-start px-5 py-3 rtl:[clip-path:polygon(0_0,calc(100%-12px)_0,100%_50%,calc(100%-12px)_100%,0_100%)] ltr:[clip-path:polygon(12px_0,100%_0,100%_100%,12px_100%,0_50%)]">
      <span className="font-serif text-4xl font-bold leading-none text-blue"><bdi>{t.priceValue * qty} {t.currency}</bdi></span>
      <span className="mt-1 text-[0.75rem] font-bold text-ink-soft">{t.order.priceNote}</span>
    </div>
  </div>
);

const Field = ({ id, label, children }) => (
  <div>
    <label htmlFor={id} className="mb-1.5 block text-[0.95rem] font-bold text-ink">{label}</label>
    {children}
  </div>
);

const Success = ({ order, t, onReset }) => (
  <motion.div data-testid="order-success" className="paper-card grain relative p-8 text-center sm:p-10" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: EASE }}>
    <Tape className="-top-3 start-1/2 -translate-x-1/2 rotate-[2deg]" />
    <CheckCircle2 size={44} className="mx-auto text-sage" strokeWidth={1.6} />
    <h3 className="font-serif mt-4 text-3xl font-bold text-blue">{t.order.success.title}</h3>
    <p className="mt-3 text-[1.05rem] leading-[1.85] text-ink-soft">{t.order.success.body}</p>
    <p className="mt-5 text-xs font-semibold text-ink-soft">{t.order.success.ref} · <bdi data-testid="order-reference" dir="ltr">{order.id}</bdi></p>
    <button type="button" data-testid="order-again-button" onClick={onReset} className="mt-8 min-h-[44px] rounded-full border border-ink/20 px-5 text-sm font-bold text-ink hover:bg-cream-deep">{t.order.success.again}</button>
  </motion.div>
);

const EMPTY = { full_name: "", phone: "", city: "", hp_website: "" };

export default function OrderForm() {
  const { t } = useExplorer();
  const o = t.order;
  const [form, setForm] = useState(EMPTY);
  const [qty, setQty] = useState(1);
  const [status, setStatus] = useState("idle");
  const [order, setOrder] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const { data } = await axios.post(`${API}/orders`, { ...form, quantity: qty });
      setOrder(data);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <Section id="commander" bg="bg-sage-soft">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal><Eyebrow className="mb-5">{o.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.05}><h2 className="font-serif text-4xl font-bold leading-[1.3] sm:leading-[1.3] lg:leading-[1.3] text-ink sm:text-5xl">{o.title}</h2></Reveal>
          <Reveal delay={0.1}><p className="mt-6 text-[1.08rem] leading-[1.85] text-ink-soft sm:text-lg">{o.body}</p></Reveal>
          <Reveal delay={0.15} className="mt-10"><PriceTag t={t} qty={qty} /></Reveal>
          <Reveal delay={0.2} className="mt-8 flex items-center gap-3 text-[0.95rem] font-semibold text-ink">
            <MessageCircle size={18} className="text-[#25D366]" /> {o.reassurance}
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <AnimatePresence mode="wait">
            {status === "success" ? (
              <Success key="ok" order={order} t={t} onReset={() => { setStatus("idle"); setForm(EMPTY); setQty(1); }} />
            ) : (
              <Reveal key="form" as="form" onSubmit={submit} data-testid="cod-order-form" className="paper-card grain relative space-y-5 p-6 sm:p-9">
                <Tape className="-top-3 end-10 rotate-[-4deg]" />
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field id="full_name" label={o.fields.full_name}>
                    <input id="full_name" data-testid="order-full-name" className="field" value={form.full_name} onChange={set("full_name")} placeholder={o.placeholders.full_name} required minLength={2} autoComplete="name" />
                  </Field>
                  <Field id="phone" label={o.fields.phone}>
                    <input id="phone" data-testid="order-phone" className="field text-start" type="tel" inputMode="tel" dir="ltr" value={form.phone} onChange={set("phone")} placeholder={o.placeholders.phone} required minLength={9} autoComplete="tel" />
                  </Field>
                  <Field id="city" label={o.fields.city}>
                    <input id="city" data-testid="order-city" className="field" value={form.city} onChange={set("city")} placeholder={o.placeholders.city} required minLength={2} autoComplete="address-level2" />
                  </Field>
                  <div>
                    <span className="mb-1.5 block text-[0.95rem] font-bold text-ink">{o.fields.quantity}</span>
                    <div className="inline-flex items-center rounded-full border border-ink/20 bg-cream-card" dir="ltr">
                      <button type="button" data-testid="qty-minus" aria-label="نقّص" onClick={() => setQty((q) => Math.max(1, q - 1))} className="flex h-11 w-11 items-center justify-center rounded-full text-ink hover:bg-cream-deep"><Minus size={16} /></button>
                      <span data-testid="qty-value" className="w-8 text-center font-serif text-xl font-bold">{qty}</span>
                      <button type="button" data-testid="qty-plus" aria-label="زيد" onClick={() => setQty((q) => Math.min(10, q + 1))} className="flex h-11 w-11 items-center justify-center rounded-full text-ink hover:bg-cream-deep"><Plus size={16} /></button>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between rule-dashed pt-4">
                  <span className="text-xs font-bold text-ink-soft">{o.total}</span>
                  <span data-testid="order-total" className="font-serif text-3xl font-bold text-blue"><bdi>{t.priceValue * qty} {t.currency}</bdi></span>
                </div>
                {status === "error" && <p data-testid="order-error" role="alert" className="rounded-xl bg-coral/10 px-4 py-3 text-sm font-semibold text-coral-deep">{o.error}</p>}
                <div className="grid gap-3 sm:grid-cols-[1.4fr_1fr]">
                  <button type="submit" data-testid="order-submit-button" disabled={status === "loading"} className="flex min-h-[56px] w-full items-center justify-center rounded-full bg-coral text-[1.05rem] font-bold text-cream shadow-[0_10px_24px_-10px_rgba(238,108,77,0.7)] transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-coral-deep disabled:opacity-60">
                    {status === "loading" ? o.submitting : o.submit}
                  </button>
                  <WhatsAppButton label={o.whatsapp} message={o.waMessage} className="min-h-[56px] w-full" />
                </div>
                {/* Honeypot: invisible to people, bots fill it and the server drops the order. */}
                <div aria-hidden="true" className="sr-only">
                  <label htmlFor="hp_website">Leave this field empty</label>
                  <input id="hp_website" data-testid="order-hp-website" name="hp_website" type="text" tabIndex={-1} autoComplete="off" value={form.hp_website} onChange={set("hp_website")} />
                </div>
              </Reveal>
            )}
          </AnimatePresence>
        </div>
      </div>
    </Section>
  );
}
